import { useHttpClient } from '@/composables/useHttpClient'
import { useLogError } from '@/composables/useLogError'
import type { AccessObjects, AppConfigResponse } from '@/types/CubeDto'
import { NumberUtil } from '@/util/NumberUtil'
import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

export const useAccountStore = defineStore('account', () => {
  const { logError } = useLogError()
  const { lukasApi } = useHttpClient()

  // --- State ---

  const properties = reactive({
    isDemoUser: false,
    hasProduction: false,
    isFirstLogin: false,
    hasConfirmedEmail: false,
    isSocialLogin: false,
    isRealtimeUser: true,
    canReceiveDonations: false,
    isCommunityless: false
  })

  const settings = reactive({
    ef_highcontrast: false,
  })

  const availableCubes = ref<Array<{ cubeName: string; nickName: string }>>([])

  const tariffSettings = ref<Record<string, any> | null>(null)

  // --- Computed ---

  const isDemoUser = computed(() => properties.isDemoUser)

  const isHighContrast = computed(() => settings.ef_highcontrast)

  function checkAccountProperty(property: string): boolean {
    if (property.startsWith('!')) {
      return !properties[property.slice(1) as keyof typeof properties]
    }

    return !!properties[property as keyof typeof properties]
  }

  // Takes an array of property names, returns true if all are true.
  function checkAccountProperties(propertyList: string[] | null | undefined): boolean {
    if (!propertyList) {
      return true
    }

    return propertyList.every(p => checkAccountProperty(p))
  }

  // --- Setters ---

  /**
   * Populate account state from the getAppConfig API response.
   * Called by useAppStore.init() after the getAppConfig request resolves.
   */
  function setFromAppConfig(data: AppConfigResponse) {
    const cubeAuthData = data.data.cubeAuthData
    const appConfig = data.data.appConfig

    properties.isDemoUser = cubeAuthData?.metadata?.contactState === 'demo'
    properties.hasConfirmedEmail = cubeAuthData?.metadata?.emailConfirmed ?? false
    properties.isCommunityless = data.data.appConfig.options.isCommunityless ?? false;

    if (appConfig?.tariffSettings) {
      tariffSettings.value = {
        ...appConfig.tariffSettings,
        maxPrice: Math.ceil(1.2 * NumberUtil.parseFloat(appConfig.tariffSettings.maxPriceNet) * 100),
      }
    }
  }

  /**
   * Process and store the available cubes list.
   */
  function setAvailableCubes(rawData?: AccessObjects | null) {
    if (!rawData) {
      return
    }

    availableCubes.value = Object.entries(rawData).map(([cubeName, nickName]) => ({
      cubeName,
      nickName,
    }))
  }

  function setNickname(cubeName: string, nickName: string): void {
    availableCubes.value
      .filter(c => c.cubeName == cubeName)
      .forEach(c => c.nickName = nickName)
  }

  // --- Actions ---

  async function getAppSettings() {
    try {
      const result = await lukasApi.get('/users/settingslist')
      if (!result?.data) { return }
      applySettings(result.data)
    } catch (err) {
      logError('Error requesting app settings: ' + err)
    }
  }

  async function setHighContrast(value: boolean) {
    try {
      const postData = new FormData()
      postData.append('wert', value.toString())
      await lukasApi.post('/users/settings/ef_highcontrast', postData)
      settings.ef_highcontrast = value
    } catch (err) {
      logError('Error setting app setting highContrast: ' + err)
    }
  }

  // --- Internal helpers ---


  function applySettings(data: { list: Array<{ variable: string; wert: string }> }) {
    data.list.forEach((setting) => {
      let value: any = setting.wert
      switch (setting.variable) {
        case 'ef_highcontrast':
          value = setting.wert === 'true'
          break
      }
      if (setting.variable in settings) {
        settings[setting.variable as keyof typeof settings] = value
      }
    })
  }

  // --- Lifecycle ---

  /** Called by useAuthStore.clearAuth() on logout. */
  function reset() {
    properties.isDemoUser = false
    properties.hasProduction = false
    properties.isFirstLogin = false
    properties.hasConfirmedEmail = false
    properties.isSocialLogin = false
    properties.isRealtimeUser = true
    properties.canReceiveDonations = false
    properties.isCommunityless = false

    settings.ef_highcontrast = false
    availableCubes.value = []
    tariffSettings.value = null
  }

  return {
    // State
    properties,
    settings,
    availableCubes,
    tariffSettings,
    // Computed
    isDemoUser,
    isHighContrast,
    checkAccountProperty,
    checkAccountProperties,
    // Setters
    setFromAppConfig,
    setAvailableCubes,
    // Actions
    getAppSettings,
    setHighContrast,
    // Internal helpers
    applySettings,
    // Lifecycle
    reset,
    setNickname
  }
})
